import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

type Question = {
  id: string;
  label: string;
  field_key: string;
  kind: "text" | "email" | "phone" | "bool" | "select" | "multi";
  required: boolean;
  options: any[];
  position: number;
};

export default function EventSignup() {
  const { token } = useParams();

  // core event/form state
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [startAt, setStartAt] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [qs, setQs] = useState<Question[]>([]);

  // NEW: branding/welcome
  const [welcome, setWelcome] = useState<string>("");
  const [brandUrl, setBrandUrl] = useState<string>("");

  // attendee fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState<Record<string, any>>({});

  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc("get_event_form", { p_token: token });
      if (error) {
        setErr(error.message);
        setLoading(false);
        return;
      }
      if (!data || data.length === 0) {
        setErr("This check-in link is invalid or expired.");
        setLoading(false);
        return;
      }
      const row = data[0];
      setTitle(row.title ?? "Event");
      setDescription(row.description ?? "");
      setStartAt(row.start_at ? new Date(row.start_at).toLocaleString() : "");
      setLocation(row.location ?? "");
      setIsOpen(!!row.is_open);
      setQs((row.questions ?? []) as Question[]);
      // NEW
      setWelcome(row.welcome_message ?? "");
      setBrandUrl(row.brand_image_url ?? "");

      setLoading(false);
    })();
  }, [token]);

  const canSubmit = useMemo(() => {
    if (!isOpen) return false;
    if (!name.trim()) return false;
    for (const q of qs) {
      const v = answers[q.field_key];
      if (q.required && (v == null || v === "" || (Array.isArray(v) && v.length === 0))) {
        return false;
      }
    }
    return true;
  }, [isOpen, name, qs, answers]);

  async function submit() {
    setMsg(null);
    setErr(null);
    const { error } = await supabase.rpc("submit_event_signup", {
      p_token: token,
      p_name: name || null,
      p_email: email || null,
      p_phone: phone || null,
      p_answers: answers,
    });
    if (error) {
      setErr(error.message);
      return;
    }
    setMsg("Thanks! You’re checked in.");
    setName("");
    setEmail("");
    setPhone("");
    setAnswers({});
  }

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!isOpen) return <div style={{ padding: 16 }}>This check-in form is currently closed.</div>;

  return (
    <div style={{ padding: 16, maxWidth: 680, margin: "0 auto" }}>
      {/* branding image (optional) */}
      {brandUrl && (
        <div style={{ marginBottom: 8, textAlign: "center" }}>
          <img
            src={brandUrl}
            alt="Event branding"
            style={{ maxWidth: 220, maxHeight: 120, width: "auto", height: "auto" }}
          />
        </div>
      )}

      <h1 style={{ margin: "8px 0", textAlign: "center" }}>{title}</h1>

      {(description || startAt || location) && (
        <div style={{ color: "#6b7280", marginBottom: 8, textAlign: "center" }}>
          {startAt ? startAt : ""}
          {location ? ` • ${location}` : ""}
          {description ? ` — ${description}` : ""}
        </div>
      )}

      {/* welcome / thank-you block */}
      {welcome && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 12,
            marginBottom: 12,
            color: "#111827",
          }}
        >
          {welcome}
        </div>
      )}

      {(msg || err) && (
        <div
          style={{
            marginBottom: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: msg ? "#ECFDF5" : "#FEF2F2",
            border: `1px solid ${msg ? "#A7F3D0" : "#FCA5A5"}`,
            color: msg ? "#065F46" : "#991B1B",
          }}
        >
          {msg || err}
        </div>
      )}

      {/* Core fields */}
      <div style={card}>
        <label style={label}>Name *</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={input}
          placeholder="Your name"
        />

        <label style={label}>Email</label>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={input}
          type="email"
          placeholder="you@example.com"
        />

        <label style={label}>Mobile</label>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          style={input}
          inputMode="tel"
          placeholder="(555) 555-5555"
        />
      </div>

      {/* Custom questions */}
      {qs.length > 0 && (
        <div style={card}>
          {qs.map((q) => (
            <div key={q.id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label style={label}>
                  {q.label}
                  {q.required ? " *" : ""}
                </label>
              </div>
              {renderInput(q, answers[q.field_key], (v) =>
                setAnswers((a) => ({ ...a, [q.field_key]: v }))
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ position: "sticky", bottom: 0, paddingTop: 8 }}>
        <div
          style={{
            background: "rgba(243,247,251,0.9)",
            backdropFilter: "blur(4px)",
            padding: 8,
          }}
        >
          <button onClick={submit} disabled={!canSubmit} style={primaryBtn}>
            {canSubmit ? "Check in" : "Complete required fields"}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderInput(q: Question, value: any, onChange: (v: any) => void) {
  const base = { ...input, width: "100%" } as React.CSSProperties;
  switch (q.kind) {
    case "text":
    case "email":
    case "phone":
      return (
        <input
          style={base}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder=""
        />
      );
    case "bool":
      return (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />{" "}
          Yes
        </label>
      );
    case "select":
      return (
        <select
          style={base}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {(q.options || []).map((o: any, idx: number) => (
            <option key={idx} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "multi":
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 8,
          }}
        >
          {(q.options || []).map((o: any, idx: number) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return (
              <label
                key={idx}
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...(arr || []), o]);
                    else onChange(arr.filter((x: any) => x !== o));
                  }}
                />{" "}
                {o}
              </label>
            );
          })}
        </div>
      );
    default:
      return null;
  }
}

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  marginBottom: 12,
};
const label: React.CSSProperties = { fontSize: 13, color: "#374151", marginBottom: 6 };
const input: React.CSSProperties = {
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
};
const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: "none",
  background: "#0B2E5B",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};
